import { useState } from 'react';
import { requestJson } from '../../api/client';
import {
  type EmployeeTasks,
  type PeriodDays,
  percent,
  type TaskRole,
  useEmployeeTasks,
} from '../../api/tasks';
import { downloadCsv } from '../../components/csv';
import { Icon } from '../../components/Icon';
import { days, formatDay, formatNumber, KpiCard, PeriodPills } from '../../components/Stats';
import { EmptyState, ErrorState, LoadingState } from '../../components/states';

const PAGE = 15;
/** Больше API не отдаёт за раз; остальное — в Bitrix24. */
const MAX_ROWS = 500;
const DAY_MS = 86_400_000;

const statusClass = (status: number) =>
  status === 4 ? 'status-info' : status === 6 ? 'status-neutral' : 'status-ok';

/** «Задачи из Bitrix24»: где сотрудник — ответственный. Изменяются задачи только в Bitrix24. */
export function TasksSection({ employeeId, fileName }: { employeeId: string; fileName: string }) {
  const [period, setPeriod] = useState<PeriodDays>(30);
  const [list, setList] = useState<'open' | 'closed'>('open');
  // Роль человека в задачах: ответственный, соисполнитель, поставил, наблюдатель.
  const [role, setRole] = useState<TaskRole>('responsible');
  const [limit, setLimit] = useState(PAGE);
  const tasks = useEmployeeTasks(employeeId, { period, list, role, limit });

  const header = (
    <div className="panel-header">
      <h2>Задачи из Bitrix24</h2>
      <PeriodPills
        value={period}
        onChange={(days) => {
          setPeriod(days);
          setLimit(PAGE);
        }}
      />
    </div>
  );

  if (tasks.isPending) {
    return (
      <section id="tasks" className="panel">
        {header}
        <LoadingState label="Загружаю задачи" />
      </section>
    );
  }
  if (tasks.isError) {
    return (
      <section id="tasks" className="panel">
        {header}
        <ErrorState error={tasks.error} onRetry={() => void tasks.refetch()} />
      </section>
    );
  }

  const data = tasks.data;
  const { stats } = data;
  const exportRows = async () => {
    const rows = (
      await requestJson<EmployeeTasks>(
        `/api/v1/employees/${employeeId}/tasks?period=${period}&list=${list}&role=${role}&limit=${MAX_ROWS}`,
      )
    ).items;
    if (list === 'open') {
      downloadCsv(
        `${fileName} — открытые задачи.csv`,
        ['ID', 'Задача', 'Статус', 'Срок', 'Просрочена', 'Постановщик', 'Ссылка'],
        rows.map((t) => [
          t.id,
          t.title,
          t.statusLabel,
          formatDay(t.deadline),
          t.overdue ? 'да' : 'нет',
          t.creator?.fullName ?? '',
          t.url,
        ]),
      );
    } else {
      downloadCsv(
        `${fileName} — закрытые задачи.csv`,
        ['ID', 'Задача', 'Закрыта', 'Срок', 'Позже срока', 'Постановщик'],
        rows.map((t) => [
          t.id,
          t.title,
          formatDay(t.closedAt),
          formatDay(t.deadline),
          t.late ? 'да' : 'нет',
          t.creator?.fullName ?? '',
        ]),
      );
    }
  };

  return (
    <section id="tasks" className="panel">
      {header}
      {!data.sync.ready ? (
        <p className="notice">
          <Icon name="clock" size={14} />
          Задачи ещё загружаются из Bitrix24: загружено {formatNumber(data.sync.loaded)}. Числа пока неполные.
        </p>
      ) : null}
      {!data.linked ? (
        <EmptyState title="Сотрудник не связан с Bitrix24">
          Задачи появятся после синхронизации сотрудников.
        </EmptyState>
      ) : (
        <>
          <div className="kpi-grid three task-kpis">
            <KpiCard
              label="Открыто"
              icon="clock"
              value={formatNumber(stats.open)}
              extra={`${percent(stats.open, stats.total)} %`}
            />
            <KpiCard
              label="Закрыто"
              icon="check"
              value={formatNumber(stats.completed)}
              extra={stats.completedLate ? `${formatNumber(stats.completedLate)} позже срока` : undefined}
              tone="good"
            />
            <KpiCard
              label="Просрочено"
              icon="alert"
              value={formatNumber(stats.overdue)}
              tone={stats.overdue ? 'bad' : undefined}
            />
          </div>

          {data.roles.length ? (
            <fieldset className="pill-row task-roles">
              <legend className="visually-hidden">Роль в задачах</legend>
              {data.roles.map((item) => (
                <button
                  key={item.role}
                  type="button"
                  className="pill-toggle"
                  aria-pressed={item.role === role}
                  onClick={() => {
                    setRole(item.role);
                    setLimit(PAGE);
                  }}
                >
                  {item.label} · {formatNumber(item.open)}
                  {item.overdue ? (
                    <span className="task-role-overdue"> · {item.overdue} просроч.</span>
                  ) : null}
                </button>
              ))}
            </fieldset>
          ) : null}

          <div className="task-toolbar">
            <fieldset className="pill-row">
              <legend className="visually-hidden">Какие задачи показать</legend>
              <button
                type="button"
                className="pill-toggle"
                aria-pressed={list === 'open'}
                onClick={() => {
                  setList('open');
                  setLimit(PAGE);
                }}
              >
                Открытые · {formatNumber(stats.open)}
              </button>
              <button
                type="button"
                className="pill-toggle"
                aria-pressed={list === 'closed'}
                onClick={() => {
                  setList('closed');
                  setLimit(PAGE);
                }}
              >
                Закрытые · {formatNumber(stats.completed)}
              </button>
            </fieldset>
            {data.total ? (
              <button type="button" className="btn-secondary" onClick={() => void exportRows()}>
                <Icon name="download" size={14} />
                Выгрузить
              </button>
            ) : null}
          </div>

          {!data.items.length ? (
            <EmptyState title={list === 'open' ? 'Открытых задач нет' : 'Закрытых задач за период нет'} />
          ) : (
            <div className="table-scroll">
              {list === 'open' ? (
                <table className="task-table">
                  <thead>
                    <tr>
                      <th>Задача</th>
                      <th>Статус</th>
                      <th className="number">Срок</th>
                      <th className="number">Висит</th>
                      <th>
                        <span className="visually-hidden">Ссылка</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((task) => (
                      <tr key={task.id}>
                        <td>
                          <strong>{task.title}</strong>
                          {task.creator?.fullName ? <small>Поставил: {task.creator.fullName}</small> : null}
                        </td>
                        <td>
                          <span className={`status ${statusClass(task.status)}`}>{task.statusLabel}</span>
                        </td>
                        <td className={`number${task.overdue ? ' bad' : ''}`}>{formatDay(task.deadline)}</td>
                        <td className="number">
                          {task.createdAt
                            ? days(
                                Math.max(
                                  0,
                                  Math.floor((Date.now() - new Date(task.createdAt).getTime()) / DAY_MS),
                                ),
                              )
                            : '—'}
                        </td>
                        <td className="link-cell">
                          <a
                            className="btn-accent"
                            href={task.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={`Открыть задачу #${task.id} в Bitrix24`}
                          >
                            <Icon name="external" size={12} />В Bitrix24
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <table className="task-table">
                  <thead>
                    <tr>
                      <th>Задача</th>
                      <th className="number">Закрыта</th>
                      <th className="number">Выполнялась</th>
                      <th>Срок</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((task) => (
                      <tr key={task.id}>
                        <td>
                          <strong>{task.title}</strong>
                          {task.creator?.fullName ? <small>Поставил: {task.creator.fullName}</small> : null}
                        </td>
                        <td className="number">{formatDay(task.closedAt)}</td>
                        <td className="number">
                          {task.createdAt && task.closedAt
                            ? days(
                                Math.max(
                                  1,
                                  Math.ceil(
                                    (new Date(task.closedAt).getTime() - new Date(task.createdAt).getTime()) /
                                      DAY_MS,
                                  ),
                                ),
                              )
                            : '—'}
                        </td>
                        <td>
                          {!task.deadline ? (
                            <span className="muted">без срока</span>
                          ) : task.late ? (
                            <span className="status status-bad">Позже срока</span>
                          ) : (
                            <span className="status status-neutral">В срок</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {data.total > data.items.length && data.items.length < MAX_ROWS ? (
            <div className="more-row">
              <button
                type="button"
                className="btn-secondary"
                disabled={tasks.isFetching}
                onClick={() => setLimit((current) => Math.min(MAX_ROWS, current + 50))}
              >
                Показать ещё · {formatNumber(data.total - data.items.length)}
              </button>
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}
